import {
  Button,
  Card,
  Eyebrow,
  Field,
  PageHeader,
  SelectField,
  StatusGlyph,
  cn,
} from '@homecare/ui';
import { useNavigate } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useApi, useRefreshClients } from '../../lib/api-hooks';
import { systemClock, type Clock } from '../../lib/clock';
import { useUnsavedChangesGuard } from '../../lib/unsaved-changes';

/**
 * Intake (§7).
 *
 * Referral → demographics → address → contacts → payer and authorization →
 * preferences and assistance profile.
 *
 * ## The draft is the client record
 *
 * §7: "save as draft at every step; an intake worker gets interrupted." There
 * is no separate draft table, and that is the design rather than a shortcut.
 *
 * Step one creates a real client in `REFERRAL` status — which is exactly what a
 * half-finished intake *is*, and what §4's machine already models. Every step
 * after it PATCHes that record. So an interrupted worker's half-entered client
 * appears in the roster as a referral, findable by the person covering for
 * them, rather than sitting in a drafts table nobody thinks to look in.
 *
 * The cost is honest and worth naming: a client abandoned at step two is a
 * referral with no address. That is a real state — referrals arrive by phone
 * with less than that — and the list shows it as one.
 *
 * ## The guard, and why it still matters
 *
 * Saving at every step does not make the unsaved-changes guard redundant.
 * Saving happens on **Continue**; typing half a page and closing the tab still
 * loses it, and §25.5's guard is what says so.
 */

const STEPS = ['Referral', 'Demographics', 'Address', 'Contacts', 'Payer', 'Preferences'] as const;

type Step = (typeof STEPS)[number];

interface Draft {
  mrn: string;
  firstName: string;
  lastName: string;
  preferredName: string;
  dateOfBirth: string;
  gender: string;
  primaryLanguage: string;
  ssnLast4: string;
  medicaidId: string;
  medicareId: string;
  referralSource: string;
  line1: string;
  city: string;
  state: string;
  postalCode: string;
  contactFirstName: string;
  contactLastName: string;
  contactRelationship: string;
  contactPhone: string;
  defaultPayerId: string;
  languagePreference: string;
  smokingHousehold: string;
}

const EMPTY: Draft = {
  mrn: '',
  firstName: '',
  lastName: '',
  preferredName: '',
  dateOfBirth: '',
  gender: '',
  primaryLanguage: '',
  ssnLast4: '',
  medicaidId: '',
  medicareId: '',
  referralSource: '',
  line1: '',
  city: '',
  state: '',
  postalCode: '',
  contactFirstName: '',
  contactLastName: '',
  contactRelationship: '',
  contactPhone: '',
  defaultPayerId: '',
  languagePreference: '',
  smokingHousehold: '',
};

export function IntakeWizard({ clock = systemClock }: { clock?: Clock } = {}): ReactNode {
  const api = useApi();
  const navigate = useNavigate();
  const refreshClients = useRefreshClients();

  const [step, setStep] = useState<Step>('Referral');
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [clientId, setClientId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useUnsavedChangesGuard(dirty);

  const index = STEPS.indexOf(step);
  const set = (field: keyof Draft, value: string): void => {
    setDraft((was) => ({ ...was, [field]: value }));
    setDirty(true);
  };

  /**
   * Save whatever this step collected, then move on.
   *
   * The first step creates; every later one patches. Both are the same client
   * record, so an interruption at any point leaves something a colleague can
   * find.
   */
  async function saveAndContinue(): Promise<void> {
    setFailure(null);
    setSaving(true);

    try {
      let id = clientId;

      if (id === null) {
        const created = await api.send<{ id: string }>('POST', '/v1/clients', {
          mrn: draft.mrn.trim(),
          firstName: draft.firstName.trim(),
          lastName: draft.lastName.trim(),
          dateOfBirth: draft.dateOfBirth,
        });

        id = created.id;
        setClientId(id);
      } else {
        await api.send('PATCH', `/v1/clients/${id}`, patchFor(step, draft));
      }

      if (step === 'Address' && draft.line1.trim()) {
        await api.send('POST', `/v1/clients/${id}/addresses`, {
          type: 'HOME',
          line1: draft.line1.trim(),
          city: draft.city.trim(),
          state: draft.state.trim().toUpperCase(),
          postalCode: draft.postalCode.trim(),
          isPrimary: true,
        });
      }

      // `relationship` is required by the contract, so a contact with a name
      // and no relationship is not written at all rather than rejected — the
      // step is optional, and half a contact is worse than none.
      if (
        step === 'Contacts' &&
        draft.contactFirstName.trim() &&
        draft.contactLastName.trim() &&
        draft.contactRelationship.trim()
      ) {
        await api.send('POST', `/v1/clients/${id}/contacts`, {
          firstName: draft.contactFirstName.trim(),
          lastName: draft.contactLastName.trim(),
          relationship: draft.contactRelationship.trim(),
          phone: draft.contactPhone.trim() || null,
          isEmergencyContact: true,
        });
      }

      /*
       * The preferences step wrote nothing at all until now.
       *
       * `patchFor` returned an empty object for this step, nothing called
       * `PUT /v1/clients/:id/preferences`, and the empty PATCH succeeded — so
       * the step showed "Saved at 3:42 pm" and discarded both answers. A form
       * that reports saving and does not save is worse than one that fails:
       * nobody re-enters what they have been told is safe.
       *
       * A PUT replaces the whole set, which is right here — at intake this
       * wizard is the only thing that has written any.
       */
      if (step === 'Preferences') {
        const preferences = [
          ...(draft.languagePreference.trim()
            ? [{ key: 'LANGUAGE' as const, value: draft.languagePreference.trim() }]
            : []),
          ...(draft.smokingHousehold
            ? [
                {
                  key: 'SMOKING' as const,
                  value:
                    draft.smokingHousehold === 'YES'
                      ? 'Smoking household'
                      : 'Non-smoking household',
                },
              ]
            : []),
        ];

        // `isHardRequirement` is left to the contract's default of false. The
        // difference between a preference and a requirement is a scheduling
        // decision with real consequences — a hard requirement can leave a
        // visit unfillable — and this form has no control that asks for it. A
        // default of true would be the wizard deciding on the agency's behalf.
        if (preferences.length > 0) {
          await api.send('PUT', `/v1/clients/${id}/preferences`, { preferences });
        }
      }

      await refreshClients();
      setDirty(false);
      setSavedAt(clock.now().toLocaleTimeString());

      if (index === STEPS.length - 1) {
        await navigate({ to: '/clients/$clientId', params: { clientId: id } });
        return;
      }

      setStep(STEPS[index + 1]!);
    } catch (error) {
      setFailure(error);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid gap-5">
      <PageHeader
        title="New client"
        meta={`Step ${index + 1} of ${STEPS.length} · ${step}`}
        action={
          savedAt ? (
            <p className="text-verified flex items-center gap-2 text-sm">
              <StatusGlyph name="check" className="h-4 w-4" />
              Saved at {savedAt}
            </p>
          ) : undefined
        }
      />

      {/* The trail. Not clickable forward — a step you have not filled in has
          nothing to show — but it says where you are and what is left, which is
          what somebody returning after an interruption needs. */}
      <ol className="flex flex-wrap gap-2" aria-label="Intake steps">
        {STEPS.map((name, position) => (
          <li key={name}>
            <button
              type="button"
              disabled={position > index}
              aria-current={position === index ? 'step' : undefined}
              onClick={() => setStep(name)}
              className={cn(
                'duration-fast ease-calm min-h-web-min rounded-md px-3 py-1.5 text-sm transition-colors',
                position === index && 'bg-brand text-ink-inv font-semibold',
                position < index && 'text-ink-2 hover:bg-surface-2',
                position > index && 'text-ink-3 cursor-not-allowed',
              )}
            >
              <span className="flex items-center gap-1.5">
                {position < index && <StatusGlyph name="check" className="h-3 w-3" />}
                {name}
              </span>
            </button>
          </li>
        ))}
      </ol>

      <ErrorNote error={failure} nextStep="Check the fields above and try again." />

      <Card>
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void saveAndContinue();
          }}
          className="grid gap-4"
        >
          {step === 'Referral' && (
            <>
              <Eyebrow>Who this is, and where they came from</Eyebrow>
              <Field
                label="Client number"
                hint="Your own numbering. It is how a re-imported roster finds this person again."
                value={draft.mrn}
                onChange={(event) => set('mrn', event.target.value)}
                required
              />
              <Field
                label="Referral source"
                hint="The hospital, agency or family member who sent them."
                value={draft.referralSource}
                onChange={(event) => set('referralSource', event.target.value)}
              />
            </>
          )}

          {step === 'Referral' || step === 'Demographics' ? (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="First name"
                  value={draft.firstName}
                  onChange={(event) => set('firstName', event.target.value)}
                  required
                />
                <Field
                  label="Last name"
                  value={draft.lastName}
                  onChange={(event) => set('lastName', event.target.value)}
                  required
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Date of birth"
                  type="date"
                  value={draft.dateOfBirth}
                  onChange={(event) => set('dateOfBirth', event.target.value)}
                  required
                />
                <Field
                  label="Goes by"
                  hint="What a caregiver should call them."
                  value={draft.preferredName}
                  onChange={(event) => set('preferredName', event.target.value)}
                />
              </div>
            </>
          ) : null}

          {step === 'Demographics' && (
            <div className="grid gap-4 sm:grid-cols-2">
              <SelectField
                label="Gender"
                value={draft.gender}
                onChange={(event) => set('gender', event.target.value)}
              >
                <option value="">Not recorded</option>
                <option value="FEMALE">Female</option>
                <option value="MALE">Male</option>
                <option value="NON_BINARY">Non-binary</option>
                <option value="OTHER">Other</option>
                <option value="DECLINED">Declined to say</option>
              </SelectField>
              <Field
                label="Primary language"
                hint="Two-letter code, for matching a caregiver who speaks it."
                value={draft.primaryLanguage}
                onChange={(event) => set('primaryLanguage', event.target.value)}
              />
            </div>
          )}

          {/*
            The three identifiers eligibility is checked against (slice-02 C1).

            They were on the API, on the contract and redacted correctly, and
            the wizard collected none of them — so a client taken by phone could
            not carry the number a biller checks Medicaid eligibility with, and
            the only way in was the CSV import. Exit criterion 7 had to import
            its own fixture to have anything to redact.

            All three are PHI behind `client:read_phi`. They are on the
            Demographics step rather than a step of their own because that is
            where the intake form the partner actually uses puts them —
            alongside the name and the date of birth, in one pass.
          */}
          {step === 'Demographics' && (
            <fieldset className="grid gap-4">
              <legend className="text-ink text-sm font-medium">Identifiers</legend>
              <p className="text-ink-3 -mt-2 text-xs">
                Used to check eligibility and to bill. Only people with clinical-records access can
                see these.
              </p>

              <div className="grid gap-4 sm:grid-cols-3">
                <Field
                  label="Medicaid ID"
                  value={draft.medicaidId}
                  onChange={(event) => set('medicaidId', event.target.value)}
                />
                <Field
                  label="Medicare ID"
                  value={draft.medicareId}
                  onChange={(event) => set('medicareId', event.target.value)}
                />
                <Field
                  label="SSN last 4"
                  // Four digits, and only four. The full number is never asked
                  // for and has nowhere to be stored — `client.ssn_last4` is
                  // `CHAR(4)` and the contract refuses anything else.
                  inputMode="numeric"
                  maxLength={4}
                  hint="The last four digits only."
                  error={
                    draft.ssnLast4 !== '' && !/^\d{4}$/.test(draft.ssnLast4)
                      ? 'Four digits, or leave it blank.'
                      : undefined
                  }
                  value={draft.ssnLast4}
                  onChange={(event) => set('ssnLast4', event.target.value)}
                />
              </div>
            </fieldset>
          )}

          {step === 'Address' && (
            <>
              <Eyebrow>Where visits happen</Eyebrow>
              <Field
                label="Street address"
                value={draft.line1}
                onChange={(event) => set('line1', event.target.value)}
              />
              <div className="grid gap-4 sm:grid-cols-3">
                <Field
                  label="City"
                  value={draft.city}
                  onChange={(event) => set('city', event.target.value)}
                />
                <Field
                  label="State"
                  maxLength={2}
                  value={draft.state}
                  onChange={(event) => set('state', event.target.value)}
                />
                <Field
                  label="ZIP"
                  value={draft.postalCode}
                  onChange={(event) => set('postalCode', event.target.value)}
                />
              </div>
              <p className="text-ink-3 text-sm">
                The address is geocoded after saving. If it cannot be found, it shows as failed
                rather than disappearing — a caregiver still needs to get there.
              </p>
            </>
          )}

          {step === 'Contacts' && (
            <>
              <Eyebrow>Who to call</Eyebrow>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Contact first name"
                  value={draft.contactFirstName}
                  onChange={(event) => set('contactFirstName', event.target.value)}
                />
                <Field
                  label="Contact last name"
                  value={draft.contactLastName}
                  onChange={(event) => set('contactLastName', event.target.value)}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Relationship"
                  hint="Needed to save the contact — daughter, son, neighbour."
                  value={draft.contactRelationship}
                  onChange={(event) => set('contactRelationship', event.target.value)}
                />
                <Field
                  label="Phone"
                  type="tel"
                  value={draft.contactPhone}
                  onChange={(event) => set('contactPhone', event.target.value)}
                />
              </div>
            </>
          )}

          {step === 'Payer' && (
            <>
              <Eyebrow>Who pays</Eyebrow>
              <p className="text-ink-2 text-sm">
                Set the payer that covers anything not claimed by a more specific arrangement.
                Authorisations and dual-payer rules are added on the client’s own page, where the
                remaining units are visible while you set them.
              </p>
              <PayerPicker
                value={draft.defaultPayerId}
                onChange={(value) => set('defaultPayerId', value)}
              />
            </>
          )}

          {step === 'Preferences' && (
            <>
              <Eyebrow>Preferences and assistance</Eyebrow>
              <Field
                label="Language preference"
                hint="Recorded as a preference. Whether it is a hard requirement is a scheduling decision."
                value={draft.languagePreference}
                onChange={(event) => set('languagePreference', event.target.value)}
              />
              <SelectField
                label="Smoking household"
                value={draft.smokingHousehold}
                onChange={(event) => set('smokingHousehold', event.target.value)}
              >
                <option value="">Not recorded</option>
                <option value="NO">No</option>
                <option value="YES">Yes</option>
              </SelectField>
              <p className="text-ink-3 text-sm">
                The full ADL and IADL profile is recorded on the client’s page, where there is room
                to rate each activity properly.
              </p>
            </>
          )}

          <div className="border-line mt-2 flex items-center justify-between gap-3 border-t pt-4">
            {/*
              `disabled` and `disabledReason` are one prop pair in the kit's
              types — a disabled control with no explanation is a dead end — so
              they are spread together rather than set independently.
            */}
            <Button
              variant="secondary"
              {...(index === 0
                ? ({ disabled: true, disabledReason: 'This is the first step.' } as const)
                : {})}
              onClick={() => setStep(STEPS[index - 1]!)}
            >
              Back
            </Button>

            <Button type="submit" pending={saving}>
              {index === STEPS.length - 1 ? 'Finish' : 'Save and continue'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

/** What a given step contributes to the client record. */
function patchFor(step: Step, draft: Draft): Record<string, unknown> {
  if (step === 'Demographics') {
    return {
      firstName: draft.firstName.trim(),
      lastName: draft.lastName.trim(),
      preferredName: draft.preferredName.trim() || null,
      dateOfBirth: draft.dateOfBirth,
      gender: draft.gender || null,
      primaryLanguage: draft.primaryLanguage.trim() || null,
      // Omitted rather than sent empty: `ssnLast4` is a four-digit string or
      // null, and '' is neither. The contract would refuse it and the person
      // would get a validation error for a field they deliberately left blank.
      ssnLast4: draft.ssnLast4.trim() || null,
      medicaidId: draft.medicaidId.trim() || null,
      medicareId: draft.medicareId.trim() || null,
    };
  }

  if (step === 'Payer') {
    return { defaultPayerId: draft.defaultPayerId || null };
  }

  // Address and Contacts write to their own collections; Referral created the
  // record. Nothing to patch on the client itself.
  return {};
}

function PayerPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}): ReactNode {
  const api = useApi();
  const [payers, setPayers] = useState<{ id: string; name: string }[] | null>(null);

  if (payers === null) {
    void api
      .get<{ payers: { id: string; name: string }[] }>('/v1/payers')
      .then((result) => setPayers(result.payers))
      .catch(() => setPayers([]));
  }

  return (
    <SelectField
      label="Default payer"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      hint={
        payers?.length === 0 ? 'No payers set up yet. A biller adds them under Revenue.' : undefined
      }
    >
      <option value="">Not set</option>
      {(payers ?? []).map((payer) => (
        <option key={payer.id} value={payer.id}>
          {payer.name}
        </option>
      ))}
    </SelectField>
  );
}
